import {
  type Firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
} from 'firebase/firestore';
import { type Functions, httpsCallable } from 'firebase/functions';

import { roleSchema, type Role } from '../../core/policy/roles';
import {
  RolesError,
  type CommitteeMember,
  type RolesPort,
} from '../../features/committee/application/ports/rolesPort';

/**
 * Firestore and Cloud Functions implementation of `RolesPort` (§11.1).
 */

/** The committee is a handful of people; a linked-account list is not a roster. */
const MAX_ACCOUNTS = 100;

export function createFirebaseRolesAdapter(
  firestore: Firestore,
  functions: Functions,
): RolesPort {
  const setCommitteeRoles = httpsCallable<
    { targetUid: string; roles: readonly Role[] },
    { ok: boolean }
  >(functions, 'setCommitteeRoles');

  return {
    async listLinkedAccounts(): Promise<readonly CommitteeMember[]> {
      const links = await getDocs(
        query(collection(firestore, 'userAccounts'), limit(MAX_ACCOUNTS)),
      );

      /*
       * The employee name comes from the record, one read per account.
       *
       * Bounded by the same limit as the list, and this screen is opened
       * rarely — by one person, to change one thing (§11.5.1).
       */
      return Promise.all(
        links.docs.map(async (entry) => {
          const data = entry.data();
          const employeeId = typeof data['employeeId'] === 'string' ? data['employeeId'] : '';
          const stored: unknown = data['roles'];

          const employee = employeeId
            ? await getDoc(doc(firestore, 'employees', employeeId))
            : null;
          const fullName: unknown = employee?.data()?.['fullName'];

          return {
            authUid: entry.id,
            email: typeof data['email'] === 'string' ? data['email'] : '',
            employeeId,
            fullName: typeof fullName === 'string' ? fullName : employeeId,
            // Parsed rather than cast: a role written by an older version, or
            // by hand, must not appear in a list this screen writes back.
            roles: Array.isArray(stored)
              ? stored.flatMap((value) => {
                  const parsed = roleSchema.safeParse(value);
                  return parsed.success ? [parsed.data] : [];
                })
              : [],
          };
        }),
      );
    },

    async setRoles(targetUid, roles): Promise<void> {
      try {
        await setCommitteeRoles({ targetUid, roles });
      } catch (error) {
        const message =
          typeof error === 'object' && error !== null && 'message' in error
            ? String(error.message)
            : '';

        if (message.includes('insufficient-role') || message.includes('permission')) {
          throw new RolesError('forbidden', 'لا تملك صلاحية تغيير الأدوار.');
        }
        if (message.includes('invalid-request')) {
          throw new RolesError(
            'invalid',
            'لا يمكنك سحب صلاحية الإدارة من نفسك، ولا إسناد دور غير معروف.',
          );
        }
        if (message.includes('not-found')) {
          throw new RolesError('not_found', 'هذا الحساب غير مرتبط بسجل موظف.');
        }
        throw new RolesError('unknown', 'تعذّر حفظ الأدوار.');
      }
    },
  };
}
